import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import Footer from '../components/Footer';
import conditionalImage from '../assets/form conditional.png';
import frontendQuestions from '../assets/form-frontend.png';
import useLightbox from '../components/utils/useLightbox';
import heroImage from '../assets/npphero.png';
import builtform from '../assets/built-form.png';
import emptyformbuilder from '../assets/empty-form-builder.png';
import formlist from '../assets/formlist.png';
import formfield from '../assets/form-field.png';

const HealthcareCase = () => {
  const { openLightbox, Lightbox } = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

 const stats = [
  {
    number: '1,500',
    label: 'Patient forms processed / month',
  },
  {
    number: '5 min',
    label: 'Reported form creation time',
  },
  {
    number: '1',
    label: 'Patient signature across forms',
  },
  {
    number: '5',
    label: 'Product iterations from fixed forms to connected workflows',
  },
];

  return (
    <PageTransition>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          background: 'var(--white)',
          borderBottom: '3px solid var(--black)',
          zIndex: 1000,
          padding: '1.5rem 5%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Link
          to="/"
          style={{
            fontWeight: 700,
            color: 'var(--black)',
            textDecoration: 'none',
            fontSize: '1.1rem',
          }}
        >
          ← Back to Home
        </Link>
        <div style={{ fontWeight: 900, fontSize: '1.2rem' }}>Case Study</div>
      </nav>

      <section
        style={{
          marginTop: '5rem',
          background: 'var(--black)',
          color: 'var(--white)',
          padding: '6rem 8rem',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="hero-label">Case Study 03</div>
        <h1
          style={{
            fontSize: 'clamp(3rem, 6vw, 5rem)',
            fontWeight: 900,
            lineHeight: 1,
            marginBottom: '2rem',
            letterSpacing: '-2px',
            color: 'var(--white)',
          }}
        >
          Healthcare Form Builder
        </h1>
        <div className="hero-result">
          Making healthcare forms easier to create, manage and complete
        </div>

        <div className="hero-meta">
          <div>
            <div className="meta-label">Role</div>
            <div className="meta-value">Product Designer</div>
          </div>
          <div>
            <div className="meta-label">Duration</div>
            <div className="meta-value">Jan – Jun 2024</div>
          </div>
          <div>
            <div className="meta-label">Team</div>
            <div className="meta-value">Product, engineering & client stakeholders</div>
          </div>
          <div>
            <div className="meta-label">Client</div>
            <div className="meta-value">Hospital Network</div>
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '0 8rem',
          marginTop: '-4rem',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <div className="hero-image">
          <div style={{ overflow: 'hidden' }}>
            <img
              src={heroImage}
              alt="Healthcare form builder and patient-facing form"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </div>
        </div>
      </section>

      <div className="content">
        <section className="section">
          <h2 className="section-title">The Problem</h2>
          <p className="section-text">
            A hospital network relied on multiple patient forms covering consent,
            medical history, insurance details and emergency contacts. Patients
            completed these forms as part of the visit journey, while hospital
            teams managed the information and supporting documents across
            manual processes.
          </p>
          <p className="section-text">
            This created friction for both sides. Patients encountered repeated
            questions and signature steps, while hospital staff spent time
            entering information, preparing documents and managing paperwork.
          </p>
          <p className="section-text">
            The forms also needed to evolve as regulations, insurance requirements
            and medical procedures changed. With fixed forms, even routine
            updates depended on development effort, making the process difficult
            to maintain over time.
          </p>
          <p className="section-text">
            The design opportunity was to move beyond digitising individual forms
            and create a configurable system: one that gave hospital teams more
            control over changing requirements while keeping the patient
            experience straightforward.
          </p>
        </section>

        <section className="section">
          <h2 className="section-title">The Evolution: 5 Iterations</h2>
          <p className="section-text">
            As the workflow was explored with the client, the requirements became
            more specific. The product evolved through five iterations—from
            fixed forms to a configurable system supporting conditional
            questions and dependencies across forms. Each iteration addressed
            a limitation exposed by the previous approach.
          </p>

          <div className="evolution">
            <h3>How Requirements Evolved</h3>

            <div className="iteration">
              <div className="iteration-number">Iteration 1</div>
              <h4>Fixed Forms with Salesforce Integration</h4>
              <p><strong>Initial need:</strong> Create a small set of forms and send submitted information to Salesforce.</p>
              <p><strong>What we built:</strong> Five fixed forms connected to Salesforce.</p>
              <p><strong>What we learned:</strong> Form requirements changed frequently, and each update required development support.</p>
            </div>

            <div className="iteration">
              <div className="iteration-number">Iteration 2</div>
              <h4>Editable Forms</h4>
              <p><strong>Emerging need:</strong> The hospital needed a larger set of forms that could be updated.</p>
              <p><strong>What we built:</strong> An admin experience for editing existing forms.</p>
              <p><strong>What we learned:</strong> Editing existing forms was not enough; teams also needed to create new ones.</p>
            </div>

            <div className="iteration">
              <div className="iteration-number">Iteration 3</div>
              <h4>Configurable Form Builder</h4>
              <p><strong>Emerging need:</strong> Hospital teams wanted to build their own forms.</p>
              <p><strong>What we built:</strong> A drag-and-drop builder with configurable fields, validation and publishing.</p>
              <p><strong>What we learned:</strong> Forms needed to respond to patient answers rather than display every question to everyone.</p>
            </div>

            <div className="iteration">
              <div className="iteration-number">Iteration 4</div>
              <h4>Conditional Questions</h4>
              <p><strong>Emerging need:</strong> Follow-up questions should appear only when a patient's answer made them relevant.</p>
              <p><strong>What we built:</strong> Conditional logic within an individual form.</p>
              <p><strong>What we learned:</strong> Some workflows needed to respond to answers captured in other forms, too.</p>
            </div>

            <div className="iteration">
              <div className="iteration-number">Iteration 5 · Final</div>
              <h4>Multi-page Forms with Cross-form Logic</h4>
              <p><strong>Emerging need:</strong> Separate consent and non-consent forms, with additional forms appearing when earlier answers required them.</p>
              <p><strong>Example:</strong> If a patient selected “Accident” as the cause of an injury, the accident details form became relevant.</p>
              <p><strong>What we built:</strong> A multi-form workflow with dependencies across forms.</p>
            </div>
          </div>
        </section>

        <section className="section">
          <h2 className="section-title">The Hard Part: Conditional Logic</h2>

          <div className="challenge-block">
            <h3>Design Challenge: Making Complexity Understandable</h3>
            <p>
              The form builder had to make conditional behaviour configurable for
              hospital staff without requiring them to think in technical rules.
              At the same time, patients needed to see only the follow-up
              questions relevant to their answers.
            </p>
            <p>
              The key design decision was to treat configuration and completion as
              two connected experiences: make the rules understandable for the
              people building forms, and make the resulting patient journey feel
              clear and focused.
            </p>
          </div>

          <div className="deep-dive">
            <h3>How the Conditional Builder Works</h3>
            <p><strong>For hospital staff — form builder:</strong></p>
            <ul>
              <li>Select the question that triggers the condition.</li>
              <li>Define the answer or condition that should be matched.</li>
              <li>Choose which follow-up questions should appear.</li>
              <li>Review the configured relationship between questions.</li>
              <li>Preview the patient-facing experience before publishing.</li>
            </ul>

            <p style={{ marginTop: '2rem' }}>
              <strong>For patients — form experience:</strong>
            </p>
            <ul>
              <li>Show relevant follow-up questions based on earlier answers.</li>
              <li>Keep the patient focused on the questions that apply to them.</li>
              <li>Allow patients to revisit an answer and update the dependent questions.</li>
            </ul>
          </div>

          <div className="image-grid">
            <div className="image-grid-custom-item">
              <img
                src={conditionalImage}
                alt="Conditional logic configured in the healthcare form builder"
                onClick={() => openLightbox(conditionalImage)}
                style={{ cursor: 'zoom-in', borderRadius: '8px', transition: 'transform 0.3s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              />
            </div>
            <div className="image-grid-custom-item">
              <img
                src={frontendQuestions}
                alt="Patient-facing form questions"
                onClick={() => openLightbox(frontendQuestions)}
                style={{ cursor: 'zoom-in', borderRadius: '8px', transition: 'transform 0.3s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              />
            </div>
          </div>
        </section>

        <section className="section">
          <h2 className="section-title">Cross-Form Dependencies</h2>
          <p className="section-text">
            The final iteration extended the system beyond question-level
            conditions. An answer in one form could determine whether another
            form was relevant, turning a collection of separate forms into a
            connected patient workflow.
          </p>

          <div className="deep-dive">
            <h3>Example: Accident Details Form</h3>
            <p><strong>Scenario:</strong></p>
            <ul>
              <li>The patient answers “What is the cause of your injury?” in an initial form.</li>
              <li>The patient selects “Accident”.</li>
              <li>The Accident Details form is added to the workflow.</li>
              <li>The additional form collects relevant details such as date, location, police report and witnesses.</li>
              <li>If the patient changes the original answer, the dependent form is updated accordingly.</li>
            </ul>

            <p style={{ marginTop: '2rem' }}>
              <strong>Design considerations:</strong>
            </p>
            <ul>
              <li>Make it clear when a new form has been added.</li>
              <li>Update the progress indicator when the form count changes.</li>
              <li>Guide the patient to the newly relevant form.</li>
              <li>Preserve entered information when a dependent form is removed, where the workflow allows it to be restored.</li>
            </ul>
          </div>
        </section>

        <div className="image-grid">
          <div className="image-grid-item">
            <img
              src={formlist}
              alt="Centralised healthcare forms library"
              onClick={() => openLightbox(formlist)}
              style={{ cursor: 'zoom-in', borderRadius: '8px', transition: 'transform 0.3s ease', width: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
            <p>Centralised Forms Library</p>
          </div>

          <div className="image-grid-item">
            <img
              src={emptyformbuilder}
              alt="Drag-and-drop healthcare form builder"
              onClick={() => openLightbox(emptyformbuilder)}
              style={{ cursor: 'zoom-in', borderRadius: '8px', transition: 'transform 0.3s ease', width: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
            <p>Drag-and-Drop Form Builder</p>
          </div>

          <div className="image-grid-item">
            <img
              src={formfield}
              alt="Form field configuration and validation settings"
              onClick={() => openLightbox(formfield)}
              style={{ cursor: 'zoom-in', borderRadius: '8px', transition: 'transform 0.3s ease', width: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
            <p>Field Configuration &amp; Validation</p>
          </div>

          <div className="image-grid-item">
            <img
              src={builtform}
              alt="Configured healthcare form"
              onClick={() => openLightbox(builtform)}
              style={{ cursor: 'zoom-in', borderRadius: '8px', transition: 'transform 0.3s ease', width: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
            <p>Configured Patient Form</p>
          </div>
        </div>

        <section className="section">
          <h2 className="section-title">Impact &amp; Results</h2>
          <p className="section-text">
            The platform was completed in June 2024. Project reporting indicated
            that approximately 1,500 patient forms were being processed each
            month. The outcomes below describe the intended operational value
            of the delivered workflow.
          </p>

          <div className="stats-showcase">
            {stats.map((stat) => (
              <div className="stat-big" key={stat.label}>
                <div className="number">{stat.number}</div>
                <div className="label">{stat.label}</div>
              </div>
            ))}
          </div>

          <p className="section-text" style={{ marginTop: '3rem' }}>
            <strong>For hospital teams:</strong> The configurable builder reduced
            reliance on development support for routine form creation and
            updates.
          </p>
          <p className="section-text">
            <strong>For patients:</strong> The consolidated signing flow reduced
            repeated signature steps, and digital completion made it possible
            to provide information before arriving at the hospital.
          </p>
          <p className="section-text">
            <strong>For operations:</strong> Form submissions connected to the
            existing Salesforce workflow, while PDF generation became part of
            the automated process.
          </p>
        </section>

        <div className="lessons">
          <h3>What I Learned</h3>
          <ul>
            <li><strong>Design for change, not just the first brief:</strong> The five iterations showed how a flexible product model can respond as enterprise requirements become clearer.</li>
            <li><strong>Translate operational needs into product capabilities:</strong> Hospital staff described real situations. The design challenge was to turn those situations into rules that could be configured and understood.</li>
            <li><strong>Balance flexibility with usability:</strong> The system needed to support advanced configuration without making everyday form creation intimidating.</li>
            <li><strong>Think across the service, not just the screen:</strong> The provider configuration experience and patient completion journey had to work as one connected service.</li>
            <li><strong>Use iteration to resolve ambiguity:</strong> Evolving requirements made it important to revisit assumptions and adapt the product direction as new workflow needs emerged.</li>
            <li><strong>Prioritise meaningful friction:</strong> Consolidating repeated patient steps addressed a tangible pain point in the overall experience.</li>
          </ul>
        </div>
      </div>

      <section className="next-project">
        <h2>View All Projects</h2>
        <p style={{ marginBottom: '3rem', fontSize: '1.2rem', color: 'var(--gray)' }}>
          See the complete portfolio
        </p>
        <Link to="/" className="next-btn">← Back to Home</Link>
      </section>

      <Footer />
      <Lightbox />
    </PageTransition>
  );
};

export default HealthcareCase;
